import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Chip, IconButton, Tooltip, Stack,
  AppBar, Toolbar, Snackbar, Alert, Dialog, DialogTitle, TextField,
  DialogContent, DialogActions
} from '@mui/material';
import {
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  AddCircleOutline as BackupIcon,
  FolderZip as FileIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import api from '../api/api';
import { esRolAdmin } from '../utils/authToken';

const BackupGestion = () => {
  // Estados principales
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [error, setError] = useState(null);

  // Modal / Feedback
  const [confirmModal, setConfirmModal] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Modal de Contraseña para Descarga
  const [downloadModal, setDownloadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');

  // Cargar listado de backups desde el backend
  const fetchBackups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/backup/listar');
      setBackups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error al listar respaldos:', err);
      const detail = err.response?.data?.detail;
      let mensaje = 'Error al cargar la lista de respaldos';

      try {
        if (typeof detail === 'string') {
          mensaje = detail;
        } else if (Array.isArray(detail)) {
          mensaje = detail
            .map((e) => (typeof e === 'string' ? e : e?.msg || JSON.stringify(e)))
            .join(' | ');
        } else if (detail && typeof detail === 'object') {
          mensaje = detail.msg || JSON.stringify(detail);
        } else if (err.message) {
          mensaje = String(err.message);
        }
      } catch (parseError) {
        mensaje = 'Ocurrió un error inesperado al procesar la respuesta.';
      }

      setError(typeof mensaje === 'string' ? mensaje : JSON.stringify(mensaje));
      setBackups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  // Manejo de Notificaciones
  const mostrarMensaje = (mensaje, severidad = 'success') => {
    setSnackbarMessage(mensaje);
    setSnackbarSeverity(severidad);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Generar nuevo respaldo
  const handleGenerarBackup = async () => {
    setConfirmModal(false);
    setGenerating(true);
    try {
      const res = await api.post('/backup/generar');
      mostrarMensaje(res.data?.mensaje || 'Copia de seguridad creada con éxito');
      await fetchBackups();
    } catch (err) {
      console.error('Error creando backup:', err);
      const msg = err.response?.data?.detail || err.message || 'No se pudo generar la copia de seguridad';
      mostrarMensaje(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Descargar archivo .sql / .dump vía Blob
  // 1. Abrir modal si es Rol 2
  const abrirModalDescarga = (nombreArchivo) => {
    if (!esRolAdmin()) {
      mostrarMensaje('Acceso denegado: solo el Administrador (Rol 2) puede descargar respaldos.', 'error');
      return;
    }
    setSelectedFile(nombreArchivo);
    setAdminPassword('');
    setDownloadModal(true);
  };

  // 2. Ejecutar la descarga llamando a POST /backup/descargar enviando la clave
  const handleConfirmarDescarga = async () => {
    if (!adminPassword.trim()) {
      mostrarMensaje('Ingresa tu contraseña para continuar.', 'warning');
      return;
    }

    try {
      setDownloadingFile(selectedFile);
      setDownloadModal(false);

      const res = await api.post(
        '/backup/descargar',
        {
          nombre_archivo: selectedFile,
          password_usuario: adminPassword
        },
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      mostrarMensaje(`Descarga de ${selectedFile} iniciada.`);
    } catch (err) {
      console.error('Error al descargar el archivo:', err);
      
      // Manejar respuesta de error cuando viene como Blob
      let errorMsg = 'No se pudo descargar el archivo de respaldo';
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        try {
          const parsed = JSON.parse(text);
          errorMsg = parsed.detail || errorMsg;
        } catch (_) {}
      } else if (err.response?.data?.detail) {
        errorMsg = err.response.data.detail;
      }
      
      mostrarMensaje(errorMsg, 'error');
    } finally {
      setDownloadingFile(null);
      setAdminPassword('');
      setSelectedFile(null);
    }
  };

  // Cálculos de métricas globales
  const totalArchivos = backups.length;
  const pesoTotalMB = backups
    .reduce((acc, curr) => acc + (Number(curr.tamano_mb) || 0), 0)
    .toFixed(2);
  const ultimoRespaldo = backups.length > 0 ? backups[0].fecha_creacion : 'Sin registros';

  return (
    <Box sx={{ p: 3 }}>
      {/* Encabezado AppBar de la aplicación */}
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <StorageIcon sx={{ mr: 2 }} />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Copias de Seguridad (Base de datos)
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={fetchBackups}
            disabled={loading || generating}
            size="small"
          >
            Actualizar
          </Button>
        </Toolbar>
      </AppBar>

      {/* Acciones Principales */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          🗄️  Descarga tu respaldo y guárdalo en un lugar seguro (disco externo, usb, etc.). No lo almacenes en este PC. 
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <BackupIcon />}
          onClick={() => setConfirmModal(true)}
          disabled={loading || generating}
        >
          {generating ? 'Generando Copia...' : 'Crear Nuevo Respaldo'}
        </Button>
      </Box>

      {/* Cards de Métricas / Resumen */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Total Respaldos Almacenados
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {totalArchivos}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Espacio Ocupado
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {pesoTotalMB} MB
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Última Copia de Seguridad
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {ultimoRespaldo}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Indicador de Carga */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Mensaje de Error */}
      {error && !loading && (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography>{error}</Typography>
        </Paper>
      )}

      {/* Tabla con la lista de backups */}
      {!loading && !error && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileIcon color="action" /> Archivos de Respaldo Disponibles
            </Typography>

            {backups.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  No hay archivos de respaldo generados en el servidor.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<BackupIcon />}
                  onClick={() => setConfirmModal(true)}
                  sx={{ mt: 2 }}
                >
                  Generar el primer respaldo
                </Button>
              </Box>
            ) : (
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                      <TableCell><strong>Nombre del Archivo</strong></TableCell>
                      <TableCell align="center"><strong>Fecha de Creación</strong></TableCell>
                      <TableCell align="center"><strong>Tamaño</strong></TableCell>
                      <TableCell align="center"><strong>Estado</strong></TableCell>
                      <TableCell align="right"><strong>Acciones</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backups.map((item) => (
                      <TableRow
                        key={item.nombre}
                        sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {item.nombre}
                        </TableCell>
                        <TableCell align="center">{item.fecha_creacion}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${item.tamano_mb} MB`}
                            size="small"
                            variant="outlined"
                            color="info"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<SuccessIcon fontSize="small" />}
                            label="Completado"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Descargar respaldo al equipo">
                            <span>
                              {/* CAMBIAR EL ONCLICK DEL ICONBUTTON: */}
<IconButton
  color="primary"
  onClick={() => abrirModalDescarga(item.nombre)}
  disabled={downloadingFile === item.nombre}
>
                                {downloadingFile === item.nombre ? (
                                  <CircularProgress size={22} />
                                ) : (
                                  <DownloadIcon />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modal Confirmación de Respaldo */}
      <Dialog open={confirmModal} onClose={() => setConfirmModal(false)}>
        <DialogTitle>Confirmar Copia de Seguridad</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Deseas iniciar la generación de una nueva copia de respaldo completa de la base de datos?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Esta operación ejecuta <strong>pg_dump</strong> en el servidor y guardará un archivo comprimido SQL en la carpeta de respaldos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmModal(false)}>Cancelar</Button>
          <Button onClick={handleGenerarBackup} variant="contained" color="primary">
            Sí, Generar Respaldo
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal Confirmación de Contraseña para Descargar */}
      <Dialog open={downloadModal} onClose={() => setDownloadModal(false)}>
        <DialogTitle>Confirmar Descarga de Respaldo</DialogTitle>
        <DialogContent sx={{ minWidth: 350, pt: 1 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Por razones de seguridad, ingresa tu contraseña de Administrador para descargar el archivo:
            <br />
            <strong>{selectedFile}</strong>
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Contraseña"
            variant="outlined"
            size="small"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleConfirmarDescarga()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDownloadModal(false)}>Cancelar</Button>
          <Button onClick={handleConfirmarDescarga} variant="contained" color="primary">
            Confirmar y Descargar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BackupGestion;